import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  CircularProgress,
  Fade,
  Divider,
  FormHelperText,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';

// Styled gradient background container
const GradientWrapper = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 40%, #111827 100%)`,
  position: 'relative',
  overflow: 'hidden'
}));

// Soft radial highlight effect
const RadialGlow = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: 600,
  height: 600,
  background: `radial-gradient(circle at 30% 30%, ${theme.palette.primary.light}20, transparent 70%)`,
  top: -200,
  right: -150,
  pointerEvents: 'none'
}));

export const LoginPage = () => {
  const { login, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Support both pathname and full path (with search params)
  const from = location.state?.from || '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState({ email: false, password: false });

  // Derived validation states
  const emailError = touched.email && !/^\S+@\S+\.\S+$/.test(email || '');
  const passwordError = touched.password && (password?.length < 6);
  const canSubmit = email && password && !emailError && !passwordError && !submitting;

  useEffect(() => {
    if (!loading && user) {
      navigate(from, { replace: true });
    }
  }, [loading, user, from, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GradientWrapper>
      <RadialGlow />
      <Fade in timeout={600}>
        <Box sx={{ width: '100%', maxWidth: 440 }}>
          <Card elevation={8} sx={{ backdropFilter: 'blur(6px)', background: 'rgba(17,25,40,0.85)', color: 'white', borderRadius: 3 }}>
            <CardHeader
              avatar={
                <Avatar
                  src="/logo.png"
                  alt="OptoDesk Logo"
                  sx={{ width: 64, height: 64, bgcolor: 'transparent' }}
                  slotProps={{
                    img: { style: { objectFit: 'contain' } }
                  }}
                />
              }
              title={
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{
                    background: 'linear-gradient(90deg, #FFFFFF 0%, #CCE7FF 50%, #66B2FF 100%)',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    letterSpacing: 0.5,
                    mb: 0.5
                  }}>
                    OptoDesk
                  </Typography>
                  <Typography variant="h6" fontWeight={500} sx={{ color: 'grey.200', fontSize: 18 }}>
                    Welcome Back
                  </Typography>
                </Box>
              }
              subheader={<Typography variant="body2" sx={{ color: 'grey.400' }}>Sign in to continue</Typography>}
            />
            <Divider sx={{ opacity: 0.2 }} />
            <CardContent component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
              <TextField
                label="Email"
                variant="outlined"
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, email: true }))}
                error={Boolean(emailError)}
                helperText={emailError ? 'Enter a valid email address' : ' '}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-input': { color: 'grey.100' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.45)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.light' },
                  '& .MuiInputLabel-root': { color: 'grey.300' },
                  '& .MuiInputLabel-root.Mui-focused': { color: 'primary.light' },
                  '& .MuiFormHelperText-root': { color: emailError ? 'error.main' : 'grey.500' }
                }}
              />
              <TextField
                label="Password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, password: true }))}
                error={Boolean(passwordError)}
                helperText={passwordError ? 'Minimum 6 characters' : ' '}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword(s => !s)}
                        edge="end"
                        size="small"
                        sx={{ color: 'grey.300' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiInputBase-input': { color: 'grey.100' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.45)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.light' },
                  '& .MuiInputLabel-root': { color: 'grey.300' },
                  '& .MuiInputLabel-root.Mui-focused': { color: 'primary.light' },
                  '& .MuiFormHelperText-root': { color: passwordError ? 'error.main' : 'grey.500' }
                }}
              />
              {error && (
                <FormHelperText error sx={{ fontSize: 13, mt: -0.5, mb: 1 }}>
                  {error}
                </FormHelperText>
              )}
              <Box sx={{ mt: 1.5, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  disabled={!canSubmit}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    boxShadow: '0 4px 18px -4px rgba(0,0,0,0.4)'
                  }}
                >
                  {submitting ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
                </Button>
              </Box>
            </CardContent>
          </Card>
          <Box sx={{ textAlign: 'center', mt: 3, color: 'grey.300' }}>
            <Typography variant="caption" sx={{ letterSpacing: 0.5 }}>
              Session based secure access • Your data is protected
            </Typography>
          </Box>
        </Box>
      </Fade>
    </GradientWrapper>
  );
};

export default LoginPage;
